import { toCurrencyTruncated } from '@/utilities/formatting';
import { CompanyIntrinsicDiscount } from '@/components/CompanyIntrinsicDiscount';
import RefreshOutline from '@simplywallst/ui-core/icons/RefreshOutline';

import type { NarrativeValuatorCompany } from '../../../../hooks/narrativeValuatorCompanyFragment';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { getPath } from '@/router/utils';
import { ROUTE_NARRATIVE_COMMUNITY_PROFILE } from '@/constants/routes';
import { push } from 'redux-first-router';
import { useDeferredValue } from 'react';

export type Props = {
  company: NarrativeValuatorCompany;
  fairValue: number;
  onReset: () => void;
  owner: {
    displayName: string;
    publicId: string;
  };
  narrativeType: 'AI' | 'FAIR_VALUE' | 'LEGACY' | 'USER' | 'WARREN' | null;
  initialFairValue: number;
};

export const NarrativeFairValue = ({
  company,
  fairValue,
  onReset,
  owner,
  narrativeType,
  initialFairValue,
}: Props) => {
  const currencyIso =
    getTradingItemCurrencyISO(company.data.currencyInfo) ?? 'XXX';

  const handleOwnerNameClick = () => {
    push(
      getPath({
        type: ROUTE_NARRATIVE_COMMUNITY_PROFILE,
        payload: { public_id: owner.publicId },
      })
    );
  };

  const hasFairValueChanged = useDeferredValue(initialFairValue !== fairValue);

  return (
    <div className="rounded-t-x1 bg-p-grey-50">
      <div className="grid h-12 grid-cols-[max-content_max-content] justify-between px-x2 pt-x2">
        <div className="flex flex-wrap items-center">
          {narrativeType === 'USER' && (
            <div className="flex items-center">
              <span
                className="max-w-[150px] cursor-pointer truncate text-base font-strong text-brand-01 hover:underline"
                onClick={handleOwnerNameClick}
                title={`${owner.displayName || 'User'}'s`}
              >
                {owner.displayName || 'User'}
              </span>
              <span className="text-base font-strong text-brand-01">
                {"'s"}
              </span>
              <span className="whitespace-nowrap pl-1 text-base font-strong text-solid">
                {`Fair Value`}
              </span>
            </div>
          )}
          {narrativeType === 'AI' && (
            <span className="text-base font-strong text-solid">
              {`${owner.displayName || 'User'} Fair Value`}
            </span>
          )}
        </div>
      </div>
      <div className="grid px-x2 pb-x2 pt-x1">
        <div className="flex items-center gap-x2">
          <div className="text-mega font-strong text-solid">
            {toCurrencyTruncated(fairValue, 1, currencyIso)}
          </div>
          <div className="text-tiny text-solid">
            <CompanyIntrinsicDiscount
              lastSharePrice={company.analysisValue?.lastSharePrice ?? null}
              npvPerShare={company.analysisValue?.npvPerShare ?? null}
              fairValue={fairValue}
            />
          </div>
          {hasFairValueChanged && (
            <button
              type="button"
              onClick={onReset}
              className="solid grid grid-cols-[max-content_max-content] items-center gap-x0_25 whitespace-nowrap pl-3 text-xs text-brand-01 hover:underline focus:outline-dashed focus:outline-1 focus:outline-offset-[3px] focus:outline-p-blue-100"
            >
              <RefreshOutline className="h-x2 w-auto fill-current" />
              <span>Reset</span>
            </button>
          )}
        </div>
        <div className="h-3 pt-2 text-xs text-soft">
          {hasFairValueChanged && "You've changed Fair Value"}
        </div>
      </div>
    </div>
  );
};
